import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Wine, Leaf } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";
import { seoTitle } from "../lib/seoTitle";

const ROWS = [["ingredients", "Ingredienti"], ["allergens", "Allergeni"], ["energy", "Valori energetici"], ["producer", "Prodotto da"], ["license", "Licenza fiscale"], ["storage", "Conservazione"], ["lot", "Scadenza / Lotto"], ["after_opening", "Dopo l'apertura"]];

function Highlight({ text }) {
  const parts = (text || "").split(/(\b[A-ZÀ-Ü]{4,}(?:\s+[A-ZÀ-Ü]{2,}){0,3}\b)/g);
  return <>{parts.map((p, i) => (/^[A-ZÀ-Ü\s]{4,}$/.test(p) ? <strong key={i} className="text-[#0B2A45]">{p}</strong> : p))}</>;
}

export default function LabelPage() {
  const { slug } = useParams();
  const [l, setL] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/labels/${slug}`).then(({ data }) => setL(data)).catch(() => setErr(true)); }, [slug]);
  useSeo(l ? { title: seoTitle(l.name, "Ingredienti"), path: `/ingredienti/${l.slug}`,
    description: `Ingredienti, allergeni e informazioni di ${l.name} (${l.alc} ${l.format}). Etichetta digitale ufficiale BOTTY srl.`, jsonLd: [] } : {});
  return (
    <div className="paper-grain min-h-screen">
      <Navbar />
      <main className="max-w-2xl mx-auto px-5 py-10 sm:py-14" data-testid="label-page">
        <Link to="/ingredienti" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#0F4C81] mb-6" data-testid="label-back"><ArrowLeft className="w-4 h-4" />Tutti i prodotti</Link>
        {err && <p className="text-stone-500" data-testid="label-not-found">Scheda non trovata.</p>}
        {l && (
          <article className="bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden">
            <header className="bg-[#0B2A45] text-white px-6 sm:px-10 py-8">
              <div className="eyebrow text-[#FDE047] mb-2 flex items-center gap-2"><Leaf className="w-4 h-4" />Etichetta digitale</div>
              <h1 className="font-serif font-semibold text-3xl sm:text-4xl" data-testid="label-name">{l.name}</h1>
              <p className="mt-3 text-white/80 flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid="label-meta"><span><Wine className="inline w-4 h-4 mr-1" />Alc. {l.alc}</span><span>{l.format}</span></p>
            </header>
            {l.image_url && <img src={l.image_url} alt={l.name} className="w-full max-h-72 object-cover" />}
            <h2 className="font-serif text-xl font-semibold text-[#0B2A45] px-6 sm:px-10 pt-8">Ingredienti e informazioni</h2>
            <dl className="px-6 sm:px-10 py-6 divide-y divide-stone-100">
              {ROWS.filter(([k]) => l[k]).map(([k, lab]) => (
                <div key={k} className="grid sm:grid-cols-[160px_1fr] gap-1 sm:gap-6 py-3 text-sm" data-testid={`label-row-${k}`}>
                  <dt className="text-stone-500 font-medium">{lab}</dt>
                  <dd className="text-stone-800 leading-relaxed">{k === "ingredients" ? <Highlight text={l[k]} /> : l[k]}</dd>
                </div>
              ))}
            </dl>
            {l.note && <p className="px-6 sm:px-10 pb-8 text-xs uppercase tracking-wider text-stone-400" data-testid="label-note">{l.note}</p>}
          </article>
        )}
        <p className="mt-8 text-center text-xs text-stone-400">Guatemalan Spirits &amp; Fruit Juice — BOTTY srl · <Link to="/" className="underline">guatemalanspirit.it</Link></p>
      </main>
      <Footer />
    </div>
  );
}
